import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { apiGet, apiPost } from '../../services/api';

const TYPE_LABELS = {
  job_rejected: 'Job rejected',
  job_edit_requested: 'Job edit requested',
  profile_rejected: 'Profile rejected',
  profile_edit_requested: 'Profile edit requested',
  general: 'General',
};

function formatMessageDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function CompanyMessages({ token, onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);

  const loadMessages = useCallback(
    async (page = 1, append = false) => {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const res = await apiGet(`/api/v1/company/messages?page=${page}`, token);
        if (!res.ok) {
          setError('Could not load messages.');
          return;
        }
        const json = await res.json();
        const rows = json.data ?? [];
        setMessages((prev) => (append ? [...prev, ...rows] : rows));
        setCurrentPage(json.meta?.current_page ?? page);
        setLastPage(json.meta?.last_page ?? 1);
        const nextUnread = json.meta?.unread_count ?? 0;
        setUnreadCount(nextUnread);
        onUnreadChange?.(nextUnread);
      } catch {
        setError('Could not load messages.');
      } finally {
        setLoading(false);
      }
    },
    [token, onUnreadChange]
  );

  useEffect(() => {
    loadMessages(1, false);
  }, [loadMessages]);

  async function handleMessageClick(message) {
    setExpandedId(message.id);

    if (message.is_read) return;

    try {
      const res = await apiPost(`/api/v1/company/messages/${message.id}/read`, {}, token);
      if (!res.ok) return;

      setMessages((prev) =>
        prev.map((item) =>
          item.id === message.id ? { ...item, is_read: true, read_at: new Date().toISOString() } : item
        )
      );
      setUnreadCount((prev) => {
        const next = Math.max(0, prev - 1);
        onUnreadChange?.(next);
        return next;
      });
    } catch {
      // Non-blocking
    }
  }

  async function handleMarkAllRead() {
    setMarkingAll(true);
    try {
      const res = await apiPost('/api/v1/company/messages/read-all', {}, token);
      if (!res.ok) return;

      setMessages((prev) =>
        prev.map((item) => ({
          ...item,
          is_read: true,
          read_at: item.read_at || new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
      onUnreadChange?.(0);
    } finally {
      setMarkingAll(false);
    }
  }

  if (loading && messages.length === 0) {
    return (
      <div className="text-center py-4" data-testid="rwm-messages-tab">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div data-testid="rwm-messages-tab">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 className="h5 mb-0">Messages</h2>
        {messages.length > 0 ? (
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            disabled={unreadCount === 0 || markingAll}
            onClick={handleMarkAllRead}
            data-testid="rwm-messages-mark-all-read"
          >
            {markingAll ? 'Updating…' : 'Mark all as read'}
          </Button>
        ) : null}
      </div>

      {error ? <p className="text-danger">{error}</p> : null}

      {!error && messages.length === 0 ? (
        <p className="text-muted mb-0">No messages from the RemoteWorkMarket team yet.</p>
      ) : null}

      <div className="rwm-company-messages__list">
        {messages.map((message) => {
          const expanded = expandedId === message.id;
          return (
            <div
              key={message.id}
              className={`rwm-company-messages__item border rounded mb-3 p-3 ${
                message.is_read ? '' : 'rwm-company-messages__item--unread'
              }`}
              data-testid={`rwm-message-item-${message.id}`}
            >
              <button
                type="button"
                className="rwm-company-messages__toggle btn btn-link text-start text-decoration-none p-0 w-100"
                onClick={() => handleMessageClick(message)}
              >
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <strong className="text-dark">{message.subject}</strong>
                      {!message.is_read ? (
                        <Badge bg="danger" data-testid="rwm-message-unread-badge">
                          Unread
                        </Badge>
                      ) : null}
                    </div>
                    <Badge bg="light" text="dark" className="me-2">
                      {TYPE_LABELS[message.type] || message.type}
                    </Badge>
                    <span className="text-muted small">{formatMessageDate(message.created_at)}</span>
                  </div>
                </div>
              </button>

              {expanded ? (
                <div className="mt-3">
                  <p className="mb-3 text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                    {message.body}
                  </p>
                  {message.action_url ? (
                    <Button
                      as="a"
                      href={message.action_url}
                      variant="info"
                      className="text-white"
                      data-testid={`rwm-message-action-${message.id}`}
                    >
                      Take Action
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {lastPage > currentPage ? (
        <div className="text-center mt-3">
          <Button
            type="button"
            variant="outline-secondary"
            disabled={loading}
            onClick={() => loadMessages(currentPage + 1, true)}
          >
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
